import { useMemo } from 'react';

import { QRCodeSVG } from 'qrcode.react';

import poweredbyLogo from '@/assets/gearbelt-poweredby-sitemate.svg';
import qrLogo from '@/assets/gearbelt-small-qrlogo-inverted.svg';
import qrGridOverlay from '@/assets/qrcodes/qr-grid-overlay.svg';
import { useUserStore } from '@/stores/user.store';
import { getAssetQrUrl } from '@/utils/asset';
import { convertLogoUrl } from '@/utils/image-url';

export const maxQRCodesPerPage = 9;

export function QRPrintDocument({
  contentRef,
  type,
  qrs,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>;
  type: 'assets' | '';
  qrs: {
    label?: string;
    qr: string;
  }[];
}) {
  const qrChunks = useMemo(
    () =>
      Array.from({ length: Math.ceil(qrs.length / maxQRCodesPerPage) }, (_, chunkIndex) => {
        const currentChunk = chunkIndex * maxQRCodesPerPage;
        const nextChunk = currentChunk + maxQRCodesPerPage;
        return qrs.slice(currentChunk, nextChunk);
      }),
    [qrs],
  );

  return (
    <>
      <div className="hidden print:block">
        <div ref={contentRef}>
          {qrChunks.map((chunk, pageIndex) => (
            <div key={pageIndex} className="h-screen break-after-page">
              <div className="flex h-full flex-col justify-between border border-[transparent]">
                {/* grid-rows-3, divider positions, and divider count couple to maxQRCodesPerPage ÷ grid-cols-3; update together */}
                <div className="relative grid min-h-0 flex-1 grid-cols-3 grid-rows-3 gap-0">
                  {chunk.map(({ label, qr }, index) => (
                    <QRCodeCard key={index} label={label} value={getAssetQrUrl(qr)} />
                  ))}
                  <img src={qrGridOverlay} alt="" className="pointer-events-none absolute inset-0 h-full w-full" />
                </div>
                <div className="text-default-text mt-2 flex flex-row justify-center text-xs">
                  Stick these QR codes onto your {type} so that anyone can access the asset profile and submit forms related to that asset.
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function QRCodeCard({ label, value }: { label?: string; value: string }) {
  const { org } = useUserStore();

  return (
    <div className="flex h-full w-full flex-col items-center justify-between overflow-hidden px-[23px] py-[13px]">
      <div className="flex flex-grow flex-col items-center justify-center">
        {org?.logoUrl ? (
          <img src={convertLogoUrl(org.logoUrl)} className="h-8" />
        ) : (
          <h2 className="flex min-h-[2lh] w-full max-w-40 items-center justify-center text-center text-lg font-bold">
            {/* line-clamp needs -webkit-box display, so it lives on an inner span instead of the flex h2 */}
            <span className="line-clamp-2 break-words">{org?.name}</span>
          </h2>
        )}

        {label ? (
          <div className="text-default-text my-2 flex min-h-[2lh] w-full max-w-[180px] items-start justify-center text-center text-[10px] font-bold">
            <span className="line-clamp-2 break-words">{label}</span>
          </div>
        ) : (
          <div className="border-grey text-default-text my-2 flex min-h-[2lh] w-full max-w-[180px] items-start justify-center border-b text-center text-[10px]">
            &nbsp;
          </div>
        )}

        <QRCodeSVG value={value} size={150} imageSettings={{ width: 40, height: 40, excavate: true, src: qrLogo }} level="H" />

        <h3 className="py-2">
          <img src={poweredbyLogo} alt="Powered By Sitemate" width={140} />
        </h3>
      </div>
    </div>
  );
}
